import React from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { Team, Player } from '../types/Types';
import { masters, mastersFonts, tabularNums } from '../theme/mastersTheme';

interface MyTeamProps {
  team: Team | undefined;
  teamsRevealed?: boolean;
}

const timeStampToTime = (timeStamp: number) => {
  const date = new Date(timeStamp);
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  return `${hours}:${minutes} ${ampm}`;
};

const formatScore = (score: number) => {
  if (score === 0) return 'E';
  return score > 0 ? `+${score}` : `${score}`;
};

const scoreColor = (score: number) => {
  if (score < 0) return masters.pimentoSoft;
  if (score === 0) return masters.ivory;
  return masters.ivoryMuted;
};

const railColor = (score: number, isWorst: boolean) => {
  if (isWorst) return masters.pimento;
  if (score < 0) return masters.champion;
  if (score === 0) return masters.fairway;
  return masters.ivoryLine;
};

const StatBlock: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <Typography
      sx={{
        fontFamily: mastersFonts.body,
        fontSize: { xs: '0.5rem', sm: '0.55rem' },
        fontWeight: 600,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: masters.ivoryDim,
        lineHeight: 1,
        mb: { xs: 0.25, sm: 0.4 },
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontFamily: mastersFonts.body,
        fontSize: { xs: '0.68rem', sm: '0.78rem' },
        fontWeight: 600,
        color: masters.ivory,
        lineHeight: 1.1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        ...tabularNums,
      }}
    >
      {value}
    </Typography>
  </Box>
);

const MyTeam: React.FC<MyTeamProps> = ({ team, teamsRevealed = true }) => {
  if (!team) return null;
  const maxTotalScore = Math.max(...team.players.map(p => p.totalSort));

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={{ xs: 0.75, sm: 1.5 }}>
        {team.players.map((player: Player) => {
          const isWorst = player.totalSort === maxTotalScore;
          const rail = railColor(player.totalSort, isWorst);
          const hasTeedOff = player.thru !== '';
          const thruDisplay = hasTeedOff ? player.thru : timeStampToTime(player.teeTime);
          const thruLabel = hasTeedOff ? 'Thru' : 'Tee Time';

          return (
            <Grid item xs={6} sm={6} md={4} lg={2} key={player.id}>
              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundColor: 'rgba(11, 61, 46, 0.85)',
                  border: `1px solid ${masters.ivoryLine}`,
                  borderLeft: `3px solid ${rail}`,
                  px: { xs: 1, sm: 1.5 },
                  py: { xs: 0.75, sm: 1.25 },
                  minHeight: { xs: 72, sm: 110 },
                  transition: 'border-color 0.2s ease, background-color 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(19, 83, 63, 0.9)',
                    borderColor: masters.champion,
                    borderLeftColor: rail,
                  },
                }}
              >
                {teamsRevealed ? (
                  <>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 0.5,
                        mb: { xs: 0.5, sm: 1 },
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: mastersFonts.display,
                          fontWeight: 700,
                          fontSize: { xs: '0.78rem', sm: '0.95rem' },
                          color: masters.ivory,
                          lineHeight: 1.1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {player.displayName}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: mastersFonts.body,
                          fontWeight: 700,
                          fontSize: { xs: '1.1rem', sm: '1.5rem' },
                          color: scoreColor(player.totalSort),
                          lineHeight: 1,
                          ...tabularNums,
                        }}
                      >
                        {formatScore(player.totalSort)}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        gap: { xs: 0.5, sm: 1 },
                        pt: { xs: 0.5, sm: 1 },
                        mt: 'auto',
                        borderTop: `1px solid ${masters.ivoryLine}`,
                      }}
                    >
                      <StatBlock label="Pos" value={player.position || '—'} />
                      <StatBlock label={thruLabel} value={thruDisplay || '—'} />
                    </Box>
                  </>
                ) : (
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: masters.ivoryDim,
                      fontFamily: mastersFonts.display,
                      fontSize: '1.2rem',
                      fontStyle: 'italic',
                    }}
                  >
                    —
                  </Box>
                )}
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default MyTeam;
